import { IconButton, Input } from '@apovisio/ui';
import { useDataChannel } from '@livekit/components-react';
import { Send, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { CHAT_TOPIC, MAX_CHAT_LENGTH, RateLimiter, decodeChat, encodeChat } from './chat';

interface Entry {
  key: string;
  author: string;
  text: string;
  at: number;
  mine: boolean;
}

interface ChatApi {
  entries: Entry[];
  unread: number;
  isSending: boolean;
  /** Envoie un message ; renvoie un message d'erreur à afficher, ou rien en cas de succès. */
  send: (text: string) => Promise<string | undefined>;
  markRead: () => void;
}

const MAX_ENTRIES = 200;
const time = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });
const ChatContext = createContext<ChatApi | null>(null);

/** Chat de la salle ; `null` quand l'option est désactivée (aucun abonnement au canal). */
export function useRoomChat(): ChatApi | null {
  return useContext(ChatContext);
}

/**
 * Abonnement au canal de données pendant toute la présence en salle (rien n'est stocké côté
 * serveur). Monté uniquement si le chat est actif ; schéma strict, limites de taille et de débit.
 */
export function ChatProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [unread, setUnread] = useState(0);
  const incoming = useRef(new RateLimiter(5, 10_000));
  const outgoing = useRef(new RateLimiter(5, 10_000));

  const onMessage = useCallback(
    (message: { payload: Uint8Array; from?: { identity: string; name?: string } }) => {
      const packet = decodeChat(message.payload);
      const from = message.from;
      if (!packet || !from || !incoming.current.allow(from.identity)) return;
      setEntries((current) =>
        [
          ...current,
          {
            key: `${from.identity}:${packet.id}`,
            author: from.name ?? 'Participant',
            text: packet.text,
            at: Date.now(),
            mine: false,
          },
        ].slice(-MAX_ENTRIES),
      );
      setUnread((value) => value + 1);
    },
    [],
  );
  const { send: publish, isSending } = useDataChannel(CHAT_TOPIC, onMessage);

  const send = useCallback(
    async (raw: string): Promise<string | undefined> => {
      const text = raw.trim();
      if (!text) return undefined;
      if (text.length > MAX_CHAT_LENGTH) return `${String(MAX_CHAT_LENGTH)} caractères maximum.`;
      if (!outgoing.current.allow('self')) {
        return 'Patientez quelques secondes avant d’envoyer un nouveau message.';
      }
      const id = crypto.randomUUID().replace(/-/g, '');
      try {
        await publish(encodeChat(text, id), { reliable: true });
      } catch {
        return 'Message non envoyé. Vérifiez votre connexion.';
      }
      setEntries((current) =>
        [...current, { key: `self:${id}`, author: 'Vous', text, at: Date.now(), mine: true }].slice(
          -MAX_ENTRIES,
        ),
      );
      return undefined;
    },
    [publish],
  );

  const value = useMemo<ChatApi>(
    () => ({
      entries,
      unread,
      isSending,
      send,
      markRead: () => {
        setUnread(0);
      },
    }),
    [entries, unread, isSending, send],
  );
  return <ChatContext value={value}>{children}</ChatContext>;
}

export function ChatPanel({ onClose }: { onClose: () => void }) {
  const chat = useRoomChat();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>();
  if (!chat) return null;

  async function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!chat) return;
    const failure = await chat.send(draft);
    setError(failure);
    if (!failure) setDraft('');
  }

  return (
    <aside className="app-room__panel" aria-labelledby="chat-title">
      <div className="av-row av-row--between">
        <h2 id="chat-title" className="app-room__panel-title">
          Chat
        </h2>
        <IconButton label="Fermer le chat" onClick={onClose}>
          <X />
        </IconButton>
      </div>
      <ol className="app-chat" role="log" aria-live="polite" aria-label="Messages">
        {chat.entries.length === 0 ? (
          <li className="av-text-secondary av-text-sm">
            Aucun message. Les messages ne sont pas conservés.
          </li>
        ) : null}
        {chat.entries.map((entry) => (
          <li
            key={entry.key}
            className={entry.mine ? 'app-chat__entry app-chat__entry--mine' : 'app-chat__entry'}
          >
            <span className="app-chat__meta">
              <strong>{entry.author}</strong> · {time.format(entry.at)}
            </span>
            <span className="app-chat__text">{entry.text}</span>
          </li>
        ))}
      </ol>
      <form className="app-chat__form" onSubmit={(event) => void submit(event)}>
        <Input
          label="Message"
          hideLabel
          placeholder="Écrire un message"
          value={draft}
          maxLength={MAX_CHAT_LENGTH}
          autoComplete="off"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          error={error}
        />
        <IconButton label="Envoyer" type="submit" disabled={chat.isSending || draft.trim() === ''}>
          <Send />
        </IconButton>
      </form>
    </aside>
  );
}
